import { useState } from "react";
import { toast } from "sonner";
import {
  FileText,
  Image as ImageIcon,
  Link2,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { API, api, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { uploadFileChunked } from "@/lib/upload";
import { withYear } from "@/lib/year";
import AssignDropdown from "@/components/monev/AssignDropdown";
import FieldsDropdown from "@/components/monev/FieldsDropdown";
import LastYearPanel from "@/components/monev/LastYearPanel";
import TautanDropdown from "@/components/monev/TautanDropdown";

const TYPES = [
  { key: "website", label: "Link Website", icon: Link2 },
  { key: "foto", label: "Foto", icon: ImageIcon },
  { key: "dokumen", label: "Dokumen", icon: FileText },
  { key: "teks", label: "Teks Bebas", icon: Type },
  { key: "pilihan", label: "Pilihan", icon: ListChecks },
];

export default function LeafCard({
  item,
  sub,
  allRequired,
  canFill,
  isAdmin,
  accounts,
  userKey,
  year,
  onRefresh,
  onRefreshLight,
  readOnly = false,
}) {
  const [busy, setBusy] = useState("");
  const [upPct, setUpPct] = useState(null);
  const [url, setUrl] = useState("");
  const [teksVal, setTeksVal] = useState("");
  const [ketEdit, setKetEdit] = useState(null);
  const [ketVal, setKetVal] = useState("");
  const fields = item.fields || {};
  // Halaman tautan publik membaca berkas lewat endpoint publik (tanpa login)
  const fileBase = readOnly
    ? `${API}/api/public/monev/file`
    : `${API}/api/monev/file`;

  const entries = (key) => {
    const list = sub?.[key] || [];
    return list;
  };
  const lengkap = TYPES.every(({ key }) => {
    if (!fields[key]?.enabled) return true;
    const entriesList = entries(key);
    if (!entriesList.length) return false;
    if (!fields[key].required) return true;
    return entriesList.some(
      (e) =>
        (e.by || "").startsWith("ppid") ||
        sub?.statusBy?.[e.by] === "submitted"
    );
  });

  const isWajib = (key) => {
    if (allRequired) return true;
    return fields[key]?.required;
  };

  const run = async (fn, tag, light) => {
    setBusy(tag);
    try {
      await fn();
      await (light && onRefreshLight ? onRefreshLight() : onRefresh());
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy("");
    }
  };

  const addWebsite = () =>
    run(async () => {
      await api(`/api/monev/${item.code}/website?year=${year}`, {
        method: "POST",
        body: JSON.stringify({ url: url.trim() }),
      });
      setUrl("");
    }, `web-${item.code}`, true);

  const addTeks = () =>
    run(async () => {
      await api(`/api/monev/${item.code}/teks?year=${year}`, {
        method: "POST",
        body: JSON.stringify({ text: teksVal.trim() }),
      });
      setTeksVal("");
    }, `teks-${item.code}`, true);

  const addPilihan = (val) =>
    run(async () => {
      await api(`/api/monev/${item.code}/pilihan?year=${year}`, {
        method: "POST",
        body: JSON.stringify({ value: val }),
      });
    }, `pilihan-${item.code}`, true);

  const saveKet = (key) =>
    run(async () => {
      await api(`/api/admin/monev/item/${item.code}`, {
        method: "POST",
        body: JSON.stringify({ fieldKeterangan: { [key]: ketVal } }),
      });
      setKetEdit(null);
      toast.success("Keterangan disimpan");
    }, `fields-${item.code}`);

  const del = (path) =>
    run(async () => {
      await api(path, { method: "DELETE" });
    }, `del-${item.code}`, true);

  const MAX_FILE = 30 * 1048576; // dokumen; foto dibatasi MAX_FOTO
  const MAX_FOTO = 3 * 1048576; // foto maks 3MB — lebih besar dioptimalkan dulu

  const upload = (key, file) => {
    if (!file) return;
    // Unggahan bertahap (potongan ±2MB): jaringan lambat/proxy tidak
    // lagi memutus berkas besar, kegagalan hanya mengulang potongan.
    run(async () => {
      if (file.size > MAX_FILE) {
        toast.error(
          `Ukuran berkas ${Math.round(file.size / 1048576)}MB melebihi batas 30MB. Unggah maksimal 25MB agar stabil.`
        );
        return;
      }
      let toSend = file;
      if (key === "foto" && file.size > MAX_FOTO) {
        // Kompresi bila perlu: foto >3MB dioptimalkan di browser sampai
        // ≤3MB dengan kualitas setinggi mungkin (JPEG 2000px, kualitas 85%).
        try {
          toSend = await compressImage(file);
        } catch {
          toSend = file;
        }
        // Foto yang gagal dioptimalkan TIDAK dikirim mentah — tolak jelas.
        if (toSend.size > MAX_FOTO) {
          toast.error(
            `Ukuran foto ${Math.round(file.size / 1048576)}MB melebihi batas 3MB dan gagal dioptimalkan otomatis. Simpan sebagai JPG atau perkecil tangkapan layar, lalu coba lagi.`
          );
          return;
        }
        const before = (file.size / 1048576).toFixed(1);
        const after = (toSend.size / 1048576).toFixed(2);
        toast.success(`Foto dioptimalkan otomatis: ${before}MB → ${after}MB`);
      }
      setUpPct(0);
      try {
        await uploadFileChunked({
          file: toSend,
          basePath: `/api/monev/${item.code}/file/${key}?year=${year}`,
          onProgress: (p) => setUpPct(p),
        });
      } finally {
        setUpPct(null);
      }
    }, `up-${key}-${item.code}`, true);
  };

  const setReq = (key, val) =>
    run(async () => {
      await api(`/api/admin/monev/item/${item.code}`, {
        method: "POST",
        body: JSON.stringify({ fieldRequired: { [key]: val } }),
      });
    }, `req-${key}-${item.code}`);

  const enabledCount = TYPES.filter(({ key }) => fields[key]?.enabled).length;

  // Nama pengelola yang mengisi sebuah isian; akun PPID ditampilkan "PPID".
  const byName = (e) =>
    (e?.by || "").startsWith("ppid") ? "PPID" : e?.by_label || e?.by || "";

  const setFields = (payload, done) =>
    run(async () => {
      await api(`/api/admin/monev/item/${item.code}`, {
        method: "POST",
        body: JSON.stringify({
          fieldEnabled: payload.enabled,
          teksPrompt: payload.teksPrompt,
          pilihanPrompt: payload.pilihanPrompt,
          pilihanOptions: payload.pilihanOptions,
        }),
      });
      done();
    }, `fields-${item.code}`);

  const assign = (value, done) =>
    run(async () => {
      await api(`/api/admin/monev/item/${item.code}`, {
        method: "POST",
        body: JSON.stringify({ assignedTo: value }),
      });
      done?.();
    }, `assign-${item.code}`);

  return (
    <div
      data-testid={`monev-leaf-${item.code}`}
      className="rounded-2xl border border-[#E3EBF3] bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold leading-relaxed text-[#0B2440]">
          {withYear(item.title, year)}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          <span
            data-testid={`monev-status-${item.code}`}
            className={`rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.08em] ${
              lengkap ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
            }`}
          >
            {lengkap ? "LENGKAP" : "BELUM LENGKAP"}
          </span>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
            Pengelola:
          </label>
          <AssignDropdown
            item={item}
            accounts={accounts}
            busy={busy}
            onSave={assign}
          />
          <FieldsDropdown item={item} busy={busy} onSave={setFields} />
        </div>
      )}

      <div
        className={`mt-4 grid gap-3 ${
          enabledCount >= 3
            ? "md:grid-cols-3"
            : enabledCount === 2
              ? "md:grid-cols-2"
              : ""
        }`}
      >
      {TYPES.map(({ key, label, icon: Icon }) => {
        const f = fields[key];
        if (!f?.enabled) return null;
        const list = entries(key);
        return (
          <div
            key={key}
            className="flex flex-col rounded-xl border border-[#E3EBF3] bg-[#FBFDFF] p-4"
          >
            <p className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#0B2440]">
              <Icon aria-hidden="true" className="h-3.5 w-3.5 text-[#1479BF]" />
              {key === "teks" || key === "pilihan"
                ? f.prompt || label
                : label}
              {isAdmin && canFill && !allRequired ? (
                <span
                  data-testid={`monev-required-${item.code}-${key}`}
                  className="inline-flex overflow-hidden rounded-full border border-[#E3EBF3]"
                >
                  {[
                    ["wajib", "WAJIB", true],
                    ["opsi", "OPSIONAL", false],
                  ].map(([tag, lbl, val]) => (
                    <button
                      key={tag}
                      data-testid={`monev-req-${tag}-${item.code}-${key}`}
                      disabled={busy === `req-${key}-${item.code}`}
                      onClick={() => setReq(key, val)}
                      className={`px-2 py-0.5 text-[9px] font-bold transition-colors duration-200 ${
                        f.required === val
                          ? "bg-[#1479BF] text-white"
                          : "bg-white text-slate-400 hover:text-[#1479BF]"
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </span>
              ) : (
                isWajib(key) && (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[9px] font-bold text-red-500">
                    WAJIB
                  </span>
                )
              )}
            </p>

            {key === "website" && (
              <>
                {list.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {list.map((w) => (
                      <li
                        key={w.id}
                        data-testid={`monev-website-${w.id}`}
                        className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
                      >
                        <a
                          href={w.url}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate text-xs font-medium text-[#1479BF] underline"
                        >
                          {w.url}
                        </a>
                        <span
                          data-testid={`monev-by-${w.id}`}
                          className="shrink-0 text-[10px] font-semibold italic text-slate-400"
                        >
                          ({byName(w)})
                        </span>
                        {canFill && (isAdmin || w.by === userKey) && (
                          <button
                            data-testid={`monev-website-del-${w.id}`}
                            onClick={() =>
                              del(
                                `/api/monev/${item.code}/website/${w.id}?year=${year}`
                              )
                            }
                            aria-label="Hapus link"
                            className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                          >
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canFill && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      data-testid={`monev-website-input-${item.code}`}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://alamat-website"
                      className="min-w-[180px] flex-1 rounded-lg border border-[#E3EBF3] px-3 py-2 text-xs outline-none transition focus:border-[#1479BF]"
                    />
                    <button
                      data-testid={`monev-website-add-${item.code}`}
                      onClick={addWebsite}
                      disabled={busy === `web-${item.code}` || !url.trim()}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#1479BF] px-3.5 py-2 text-[11px] font-bold text-white transition-colors hover:bg-[#0B2440] disabled:opacity-50"
                    >
                      <Plus aria-hidden="true" className="h-3.5 w-3.5" /> Tambah
                    </button>
                  </div>
                )}
              </>
            )}

            {(key === "foto" || key === "dokumen") && (
              <>
                {list.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {list.map((f2) => (
                      <li
                        key={f2.id}
                        data-testid={`monev-${key}-${f2.id}`}
                        className="flex items-center gap-2 rounded-lg bg-white px-3 py-2"
                      >
                        {key === "foto" ? (
                          <a
                            href={`${fileBase}/${f2.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={`${fileBase}/${f2.id}`}
                              alt={f2.name}
                              className="h-14 w-14 rounded-lg object-cover"
                            />
                          </a>
                        ) : (
                          <a
                            href={`${fileBase}/${f2.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-xs font-medium text-[#1479BF] underline"
                          >
                            <FileText aria-hidden="true" className="h-4 w-4" />
                            <span className="max-w-[160px] truncate">{f2.name}</span>
                            <span className="text-slate-400">
                              ({Math.max(1, Math.round(f2.size / 1024))} KB)
                            </span>
                          </a>
                        )}
                        <span
                          data-testid={`monev-by-${f2.id}`}
                          className="shrink-0 text-[10px] font-semibold italic text-slate-400"
                        >
                          ({byName(f2)})
                        </span>
                        {canFill && (isAdmin || f2.by === userKey) && (
                          <button
                            data-testid={`monev-${key}-del-${f2.id}`}
                            onClick={() =>
                              del(
                                `/api/monev/${item.code}/file/${key}/${f2.id}?year=${year}`
                              )
                            }
                            aria-label="Hapus"
                            className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                          >
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canFill && (
                  <label
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-bold ${
                      busy === `up-${key}-${item.code}`
                        ? "border border-[#BBDCF3] bg-[#EFF6FC] text-[#1479BF]"
                        : "cursor-pointer border border-[#1479BF] text-[#1479BF] transition-colors hover:bg-[#EFF6FC]"
                    }`}
                  >
                    {busy === `up-${key}-${item.code}` ? (
                      <>
                        <Loader2
                          aria-hidden="true"
                          className="h-3.5 w-3.5 animate-spin"
                        />
                        Mengunggah{upPct != null ? ` ${upPct}%` : ""}...
                      </>
                    ) : (
                      <>
                        <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                        {key === "foto" ? "Tambah Foto" : "Unggah Dokumen"}
                        <span className="text-[9px] font-medium text-slate-400">
                          · maks. {key === "foto" ? "3MB" : "25MB"}
                        </span>
                      </>
                    )}
                    <input
                      type="file"
                      hidden
                      data-testid={`monev-upload-${key}-${item.code}`}
                      disabled={busy === `up-${key}-${item.code}`}
                      accept={
                        key === "foto"
                          ? "image/jpeg,image/png,image/webp,image/gif"
                          : ".pdf,.doc,.docx,.xls,.xlsx"
                      }
                      onChange={(e) => {
                        upload(key, e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </>
            )}

            {key === "teks" && (
              <>
                {list.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {list.map((t) => (
                      <li
                        key={t.id}
                        data-testid={`monev-teks-${t.id}`}
                        className="flex items-start justify-between gap-2 rounded-lg bg-white px-3 py-2"
                      >
                        <p className="whitespace-pre-wrap break-words text-xs text-[#0B2440]">
                          {t.text}
                          <span
                            data-testid={`monev-by-${t.id}`}
                            className="ml-1 text-[10px] font-semibold italic text-slate-400"
                          >
                            ({byName(t)})
                          </span>
                        </p>
                        {canFill && (isAdmin || t.by === userKey) && (
                          <button
                            data-testid={`monev-teks-del-${t.id}`}
                            onClick={() =>
                              del(
                                `/api/monev/${item.code}/teks/${t.id}?year=${year}`
                              )
                            }
                            aria-label="Hapus teks"
                            className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                          >
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canFill && (
                  <div className="mt-2 flex flex-col gap-2">
                    <textarea
                      data-testid={`monev-teks-input-${item.code}`}
                      value={teksVal}
                      onChange={(e) => setTeksVal(e.target.value)}
                      rows={2}
                      placeholder="Tulis jawaban Anda…"
                      className="w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-xs outline-none transition focus:border-[#1479BF]"
                    />
                    <button
                      data-testid={`monev-teks-add-${item.code}`}
                      onClick={addTeks}
                      disabled={busy === `teks-${item.code}` || !teksVal.trim()}
                      className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-lg bg-[#1479BF] px-3.5 py-2 text-[11px] font-bold text-white transition-colors hover:bg-[#0B2440] disabled:opacity-50"
                    >
                      <Plus aria-hidden="true" className="h-3.5 w-3.5" /> Tambah
                    </button>
                  </div>
                )}
              </>
            )}

            {key === "pilihan" && (
              <>
                {list.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {list.map((p) => (
                      <li
                        key={p.id}
                        data-testid={`monev-pilihan-${p.id}`}
                        className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
                      >
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B2440]">
                          <ListChecks
                            aria-hidden="true"
                            className="h-3.5 w-3.5 text-[#1479BF]"
                          />
                          {p.value}
                          <span
                            data-testid={`monev-by-${p.id}`}
                            className="text-[10px] font-semibold italic text-slate-400"
                          >
                            ({byName(p)})
                          </span>
                        </span>
                        {canFill && (isAdmin || p.by === userKey) && (
                          <button
                            data-testid={`monev-pilihan-del-${p.id}`}
                            onClick={() =>
                              del(
                                `/api/monev/${item.code}/pilihan/${p.id}?year=${year}`
                              )
                            }
                            aria-label="Hapus pilihan"
                            className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                          >
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canFill && (
                  <div className="mt-2">
                    <select
                      data-testid={`monev-pilihan-select-${item.code}`}
                      value=""
                      disabled={busy === `pilihan-${item.code}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) addPilihan(v);
                      }}
                      className="w-full rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-xs outline-none transition focus:border-[#1479BF] disabled:opacity-50"
                    >
                      <option value="">— Pilih —</option>
                      {(f.options || []).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {readOnly ? (
              list.length === 0 && (
                <p
                  data-testid={`monev-kosong-${item.code}-${key}`}
                  className="mt-2 text-[11px] font-semibold italic text-slate-400"
                >
                  Belum terisi
                </p>
              )
            ) : isAdmin ? (
              ketEdit === key ? (
                <div className="mt-2">
                  <textarea
                    data-testid={`monev-ket-input-${item.code}-${key}`}
                    value={ketVal}
                    onChange={(e) => setKetVal(e.target.value)}
                    rows={2}
                    placeholder="Keterangan/petunjuk untuk pengelola…"
                    className="w-full rounded-lg border border-[#E3EBF3] px-2.5 py-1.5 text-[11px] outline-none transition focus:border-[#1479BF]"
                  />
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      data-testid={`monev-ket-save-${item.code}-${key}`}
                      onClick={() => saveKet(key)}
                      disabled={busy === `fields-${item.code}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#1479BF] px-2.5 py-1 text-[10px] font-bold text-white transition-colors hover:bg-[#0B2440] disabled:opacity-50"
                    >
                      <Save aria-hidden="true" className="h-3 w-3" /> Simpan
                    </button>
                    <button
                      type="button"
                      data-testid={`monev-ket-cancel-${item.code}-${key}`}
                      onClick={() => setKetEdit(null)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-500 transition-colors hover:text-[#0B2440]"
                    >
                      <X aria-hidden="true" className="h-3 w-3" /> Batal
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`monev-ket-${item.code}-${key}`}
                  onClick={() => {
                    setKetEdit(key);
                    setKetVal(f.keterangan || "");
                  }}
                  title="Klik untuk mengedit keterangan"
                  className="mt-2 flex items-start gap-1 text-left text-[11px] leading-relaxed text-slate-400 transition-colors hover:text-[#1479BF]"
                >
                  <Pencil aria-hidden="true" className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    {f.keterangan
                      ? withYear(f.keterangan, year)
                      : "Tambah keterangan/petunjuk"}
                  </span>
                </button>
              )
            ) : (
              f.keterangan && (
                <p
                  data-testid={`monev-ket-${item.code}-${key}`}
                  className="mt-2 text-[11px] leading-relaxed text-slate-400"
                >
                  {withYear(f.keterangan, year)}
                </p>
              )
            )}
          </div>
        );
      })}
      </div>

      {year > 2024 && (
        <LastYearPanel item={item} year={year} readOnly={readOnly} />
      )}

      {!readOnly && (
        <div className="mt-4 flex justify-end border-t border-[#E3EBF3] pt-4">
          <TautanDropdown code={item.code} year={year} />
        </div>
      )}
    </div>
  );
}